import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';
import { NgClass } from '@angular/common';
import {
  Component,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  Output,
  signal,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { StickyObserverDirective } from 'libs/shared/src/lib/directives';
import { InputFieldComponent, TextareaFieldComponent } from '../../../form';
import { FieldTypeENUM } from '../../models';
import { FormMode } from '../../models/form-mode.enum';
import { FormBuilderService } from '../../services/builder.service';
import { FieldBuilderComponent } from '../field-builder';
@Component({
  selector: 'siat-form-section-builder',
  imports: [
    MatMenuModule,
    TextareaFieldComponent,
    ReactiveFormsModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    MatTooltip,
    DragDropModule,
    forwardRef(() => FieldBuilderComponent),
    InputFieldComponent,
    StickyObserverDirective,
    NgClass,
  ],
  templateUrl: './form-section-builder.component.html',
  styleUrls: ['./form-section-builder.component.scss'],
})
export class FormSectionBuilderComponent {
  /**
   * The form group for the section.
   */
  @Input()
  sectionForm: FormGroup;

  /**
   * The index of the section.
   */
  @Input()
  sectionIndex: number;

  /**
   * The ID of the section.
   */
  @Input()
  sectionId: string;

  /**
   * The event emitter for deleting a section.
   */
  @Output()
  deleteSection = new EventEmitter<string>();

  /**
   * The event emitter for toggling drag fields.
   */
  @Output()
  dragFieldsToggle = new EventEmitter<boolean>();

  /**
   * The service for building fields.
   */
  dynamicFormService = inject(FormBuilderService);

  /**
   * Enables drag and drop for fields.
   */
  dragFieldsEnabled = false;

  /**
   * @returns {FormArray} The fields form array.
   */
  get fieldsArray(): FormArray {
    return this.sectionForm.get('fields') as FormArray;
  }

  /**
   * On click, adds a new field to the section.
   */
  addFieldToSection(): void {
    const newField = this.dynamicFormService.buildField(
      {
        id: this.generateNewId(),
        type: FieldTypeENUM.TEXT,
      },
      FormMode.Editing,
    );

    this.fieldsArray.push(newField);
  }

  /**
   * On click, duplicates a field in the section.
   */
  duplicateField(fieldFormGroup: FormGroup): void {
    const index = this.fieldsArray.controls.indexOf(fieldFormGroup);
    const duplicatedField = this.dynamicFormService.cloneFormGroup(
      fieldFormGroup,
      () => this.generateNewId(),
    );

    this.fieldsArray.insert(index + 1, duplicatedField);
  }

  /**
   * On click, deletes a field from the section.
   */
  deleteField(fieldFormGroup: FormGroup): void {
    const index = this.fieldsArray.controls.indexOf(fieldFormGroup);

    if (index !== -1) this.fieldsArray.removeAt(index);
  }

  /**
   * On click, toggles drag and drop for fields.
   */
  toggleDragFields(): void {
    this.dragFieldsEnabled = !this.dragFieldsEnabled;
    this.dragFieldsToggle.emit(this.dragFieldsEnabled);
  }

  /**
   * On field dropped event.
   * @param {CdkDragDrop<AbstractControl[]>}
   */
  onFieldDropped(event: CdkDragDrop<AbstractControl[]>): void {
    if (event.previousIndex !== event.currentIndex) {
      const from = event.previousIndex;
      const to = event.currentIndex;
      const tempControl = this.fieldsArray.at(from);

      this.fieldsArray.removeAt(from);
      this.fieldsArray.insert(to, tempControl);
      this.fieldsArray.updateValueAndValidity();
    }
  }

  /**
   * Generates a new ID for a field.
   * @returns {string} The new ID.
   */
  private generateNewId(): string {
    return this.dynamicFormService.generateId(
      `${this.sectionId}_field`,
      this.fieldsArray.length,
    );
  }

  /**
   * @param {AbstractControl} field The field to get the form group for.
   * @returns {FormGroup} The form group for the field.
   */
  getFieldFormGroup(field: AbstractControl): FormGroup {
    return field as FormGroup;
  }

  /**
   * @param {number} index The index of the field.
   * @returns {string} The ID of the field.
   */
  trackByFn = (_index: number, control: AbstractControl) =>
    control.get('id')?.value || _index;

  /**
   * @returns {boolean} Whether to disable the delete section button.
   */
  get disableDeleteSection(): boolean {
    const sectionsArray = this.sectionForm.parent as FormArray;
    return sectionsArray ? sectionsArray.length <= 1 : true;
  }

  /**
   * @returns {Signal<boolean>} Whether the section is sticky.
   */
  isSticky = signal(false);

  /**
   * @param {boolean} state Whether the section is sticky.
   */
  onSticky(state: boolean) {
    this.isSticky.set(state);
  }
}
